import React, { useState, useCallback, useEffect } from 'react';
import {Box,Container,Button,Snackbar,Alert,Stack,Typography,Dialog,DialogTitle,DialogContent,DialogActions,TextField,IconButton,InputAdornment,Pagination,MenuItem,Select,Paper,Chip,Checkbox,FormControlLabel,List,ListItem,ListItemText,CircularProgress,Divider,Card,CardContent,Switch,Tooltip,useTheme,useMediaQuery,} from '@mui/material';
import {CalendarMonth,Close,SyncAlt,CloudUpload,Refresh,Error as ErrorIcon,Schedule as ScheduleIcon,Delete as DeleteIcon,} from '@mui/icons-material';
import { Zoom } from '@mui/material';
import PageHeader from './PageHeader';
import { formatDistanceToNow } from 'date-fns';
import { getCompanyAttendanceCounts, createSyncJob, getSyncHistory, refreshKivoStatus, getKivoAttendanceCompanies, getKivoExportSchedules, upsertKivoExportSchedule, deleteKivoExportSchedule } from '../utils/api';
import { formatDate } from '../utils/dateUtils';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { keyframes } from '@mui/system';

const primary = '#40518A';

// Shimmer animation keyframes
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Shimmer effect styles
const shimmerStyles = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: `${shimmer} 1.5s ease-in-out infinite`,
};

// Progress bar striped animation for active syncs
const progressStriped = keyframes`
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 20px 0;
  }
`;

// Skeleton component for sync history table rows
const SYNC_HISTORY_GRID_COLUMNS = '60px 1fr 1fr 1.2fr 120px 120px 120px';

const SyncHistoryRowSkeleton = () => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: SYNC_HISTORY_GRID_COLUMNS,
      gap: 2,
      px: 2,
      py: 2,
      bgcolor: 'white',
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Box sx={{ width: '100%', height: 20, borderRadius: 1, ...shimmerStyles }} />
    <Box sx={{ width: '80%', height: 20, borderRadius: 1, ...shimmerStyles }} />
    <Box sx={{ width: '80%', height: 20, borderRadius: 1, ...shimmerStyles }} />
    <Box sx={{ width: '70%', height: 20, borderRadius: 1, ...shimmerStyles }} />
    <Box sx={{ width: '60px', height: 20, borderRadius: 1, ...shimmerStyles }} />
    <Box sx={{ width: '60px', height: 20, borderRadius: 1, ...shimmerStyles }} />
    <Box sx={{ width: '80px', height: 24, borderRadius: 12, ...shimmerStyles }} />
  </Box>
);

const SyncHistoryMobileCardSkeleton = () => (
  <Card variant="outlined" sx={{ borderRadius: 2 }}>
    <CardContent sx={{ p: 2 }}>
      <Stack spacing={1.25}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ width: 64, height: 18, borderRadius: 1, ...shimmerStyles }} />
          <Box sx={{ width: 96, height: 22, borderRadius: 999, ...shimmerStyles }} />
        </Box>
        <Box sx={{ width: '85%', height: 18, borderRadius: 1, ...shimmerStyles }} />
        <Box sx={{ width: '70%', height: 16, borderRadius: 1, ...shimmerStyles }} />
        <Box sx={{ width: '60%', height: 16, borderRadius: 1, ...shimmerStyles }} />
        <Box sx={{ width: '100%', height: 8, borderRadius: 999, ...shimmerStyles }} />
      </Stack>
    </CardContent>
  </Card>
);

const ExportToKivo = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { confirm } = useConfirmDialog();
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Sync with Kivo state
  const [syncKivoDialog, setSyncKivoDialog] = useState(false);
  const [syncKivoStartDate, setSyncKivoStartDate] = useState('');
  const [syncKivoEndDate, setSyncKivoEndDate] = useState('');
  const [syncKivoSubmitting, setSyncKivoSubmitting] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Pagination state for sync history
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [syncHistory, setSyncHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Recurring schedules state (interval-based, minimum 30 minutes)
  const MIN_SCHEDULE_INTERVAL = 30;
  const DEFAULT_SCHEDULE_INTERVAL = 30;
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [attendanceCompanies, setAttendanceCompanies] = useState([]);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    companyId: '',
    companyName: '',
    intervalMinutes: DEFAULT_SCHEDULE_INTERVAL,
    isEnabled: true,
  });
  // Per-card inline edits (keyed by company_id): interval draft and enabled draft
  const [intervalDrafts, setIntervalDrafts] = useState({});
  const [enabledDrafts, setEnabledDrafts] = useState({});
  const [savingScheduleId, setSavingScheduleId] = useState(null);
  const [deletingScheduleId, setDeletingScheduleId] = useState(null);

  const show = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Prefer API/auth error text (error.message / detail) over a generic fallback.
  const scheduleErrorMessage = (error, fallback) => {
    if (!error) return fallback;
    if (typeof error.message === 'string' && error.message.trim()) return error.message;
    const detail = error.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
      return detail.message || detail.msg || fallback;
    }
    return fallback;
  };

  const parseIntervalMinutesValue = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return Math.floor(n);
  };

  const storedIntervalMinutes = (schedule) => {
    const n = parseIntervalMinutesValue(schedule?.interval_minutes);
    return n != null && n >= MIN_SCHEDULE_INTERVAL ? n : DEFAULT_SCHEDULE_INTERVAL;
  };

  const validateIntervalMinutes = (value) => {
    const n = parseIntervalMinutesValue(value);
    if (n == null || n < MIN_SCHEDULE_INTERVAL) {
      return `Interval must be an integer of at least ${MIN_SCHEDULE_INTERVAL} minutes`;
    }
    return null;
  };

  const loadSchedules = useCallback(async () => {
    setLoadingSchedules(true);
    try {
      const response = await getKivoExportSchedules();
      setSchedules(response.schedules || []);
    } catch (error) {
      console.error('Error loading Kivo export schedules:', error);
    } finally {
      setLoadingSchedules(false);
    }
  }, []);

  const loadAttendanceCompanies = useCallback(async () => {
    try {
      const response = await getKivoAttendanceCompanies();
      setAttendanceCompanies(response.companies || []);
    } catch (error) {
      console.error('Error loading Kivo attendance companies:', error);
    }
  }, []);

  const handleOpenCreateSchedule = () => {
    setEditingSchedule(null);
    setScheduleForm({
      companyId: '',
      companyName: '',
      intervalMinutes: DEFAULT_SCHEDULE_INTERVAL,
      isEnabled: true,
    });
    loadAttendanceCompanies();
    setScheduleDialogOpen(true);
  };

  const getScheduleDraftInterval = (schedule) =>
    intervalDrafts[schedule.company_id] ?? storedIntervalMinutes(schedule);

  // Current enabled state shown for a card's toggle (draft overrides stored value).
  const getScheduleDraftEnabled = (schedule) =>
    enabledDrafts[schedule.company_id] ?? !!schedule.is_enabled;

  // Whether the card's interval or enabled state differs from what's stored.
  const isScheduleDirty = (schedule) => {
    const draftInterval = intervalDrafts[schedule.company_id];
    const intervalChanged =
      draftInterval != null && Number(draftInterval) !== storedIntervalMinutes(schedule);
    const enabledChanged =
      enabledDrafts[schedule.company_id] != null &&
      enabledDrafts[schedule.company_id] !== !!schedule.is_enabled;
    return intervalChanged || enabledChanged;
  };

  const handleScheduleIntervalChange = (schedule, raw) => {
    if (raw === '' || raw == null) {
      setIntervalDrafts((prev) => ({ ...prev, [schedule.company_id]: '' }));
      return;
    }
    const n = parseIntervalMinutesValue(raw);
    if (n == null) return;
    setIntervalDrafts((prev) => ({ ...prev, [schedule.company_id]: n }));
  };

  const handleScheduleEnabledChange = (schedule, nextEnabled) => {
    setEnabledDrafts((prev) => ({ ...prev, [schedule.company_id]: nextEnabled }));
  };

  const handleSaveScheduleSettings = async (schedule) => {
    const intervalError = validateIntervalMinutes(getScheduleDraftInterval(schedule));
    if (intervalError) {
      show(intervalError, 'error');
      return;
    }
    const approved = await confirm({
      title: 'Save Schedule Settings',
      message: `Save the export schedule settings for "${schedule.company_name || schedule.company_id}"?`,
      confirmLabel: 'Save',
      danger: false,
    });
    if (!approved) return;
    setSavingScheduleId(schedule.company_id);
    try {
      const response = await upsertKivoExportSchedule(schedule.company_id, {
        intervalMinutes: parseIntervalMinutesValue(getScheduleDraftInterval(schedule)),
        isEnabled: getScheduleDraftEnabled(schedule),
        companyName: schedule.company_name || undefined,
      });
      if (response.detail) {
        show(response.detail, 'error');
      } else {
        show('Schedule settings saved', 'success');
        setIntervalDrafts((prev) => {
          const next = { ...prev };
          delete next[schedule.company_id];
          return next;
        });
        setEnabledDrafts((prev) => {
          const next = { ...prev };
          delete next[schedule.company_id];
          return next;
        });
        await loadSchedules();
      }
    } catch (error) {
      console.error('Error saving schedule settings:', error);
      show(scheduleErrorMessage(error, 'Failed to save schedule settings'), 'error');
    } finally {
      setSavingScheduleId(null);
    }
  };

  const handleCloseScheduleDialog = () => {
    if (savingSchedule) return;
    setScheduleDialogOpen(false);
    setEditingSchedule(null);
  };

  const handleSaveSchedule = async () => {
    const companyId = scheduleForm.companyId;
    if (!companyId) {
      show('Please select a company', 'error');
      return;
    }
    const intervalError = validateIntervalMinutes(scheduleForm.intervalMinutes);
    if (intervalError) {
      show(intervalError, 'error');
      return;
    }
    const intervalMinutes = parseIntervalMinutesValue(scheduleForm.intervalMinutes);
    const approved = await confirm({
      title: 'Add Schedule',
      message: `Create an export schedule for "${scheduleForm.companyName || companyId}" every ${intervalMinutes} minutes?`,
      confirmLabel: 'Save',
      danger: false,
    });
    if (!approved) return;
    setSavingSchedule(true);
    try {
      const response = await upsertKivoExportSchedule(companyId, {
        intervalMinutes,
        isEnabled: scheduleForm.isEnabled,
        companyName: scheduleForm.companyName || undefined,
      });
      if (response.detail) {
        show(response.detail, 'error');
      } else {
        show('Schedule saved', 'success');
        setScheduleDialogOpen(false);
        setEditingSchedule(null);
        await loadSchedules();
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
      show(scheduleErrorMessage(error, 'Failed to save schedule'), 'error');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleDeleteSchedule = async (schedule) => {
    const approved = await confirm({
      title: 'Delete Schedule',
      message: `Delete the export schedule for "${schedule.company_name || schedule.company_id}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      danger: true,
    });
    if (!approved) return;
    setDeletingScheduleId(schedule.company_id);
    try {
      const response = await deleteKivoExportSchedule(schedule.company_id);
      if (response.detail) {
        show(response.detail, 'error');
      } else {
        show('Schedule deleted', 'success');
        await loadSchedules();
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
      show(scheduleErrorMessage(error, 'Failed to delete schedule'), 'error');
    } finally {
      setDeletingScheduleId(null);
    }
  };

  const handleOpenSyncKivo = () => {
    setSyncKivoDialog(true);
  };

  const handleCloseSyncKivo = () => {
    setSyncKivoDialog(false);
    setSyncKivoStartDate('');
    setSyncKivoEndDate('');
    setCompanies([]);
    setSelectedCompanies([]);
  };

  const handleDateChange = async () => {
    if (!syncKivoStartDate || !syncKivoEndDate) {
      return;
    }

    setLoadingCompanies(true);
    try {
      // datetime-local is local time; send UTC ISO to the backend
      const startDateISO = new Date(syncKivoStartDate).toISOString();
      const endDateISO = new Date(syncKivoEndDate).toISOString();
      
      const response = await getCompanyAttendanceCounts(startDateISO, endDateISO);
      
      if (response.companies && response.companies.length > 0) {
        setCompanies(response.companies);
        // Auto-select all companies by default
        setSelectedCompanies(response.companies.map(c => c.company_id));
      } else {
        setCompanies([]);
        setSelectedCompanies([]);
        show('No unsynced attendance records found for the selected date range', 'info');
      }
    } catch (error) {
      console.error('Error fetching company counts:', error);
      show(error.message || 'Failed to fetch company data', 'error');
      setCompanies([]);
      setSelectedCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleCompanyToggle = (companyId) => {
    setSelectedCompanies(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  const handleSelectAllCompanies = () => {
    if (selectedCompanies.length === companies.length) {
      setSelectedCompanies([]);
    } else {
      setSelectedCompanies(companies.map(c => c.company_id));
    }
  };

  const handleSyncKivoSubmit = async () => {
    if (!syncKivoStartDate || !syncKivoEndDate) {
      show('Please select both start and end dates', 'error');
      return;
    }

    if (selectedCompanies.length === 0) {
      show('Please select at least one company to sync', 'error');
      return;
    }

    const approved = await confirm({
      title: 'Sync with Kivo',
      message: `Create sync job(s) for ${selectedCompanies.length} selected company(ies) for the chosen date range?`,
      confirmLabel: 'Sync',
      danger: false,
    });
    if (!approved) return;

    setSyncKivoSubmitting(true);
    try {
      show('Creating sync jobs...', 'info');
      
      // datetime-local is local time; send UTC ISO to the backend
      const startDateISO = new Date(syncKivoStartDate).toISOString();
      const endDateISO = new Date(syncKivoEndDate).toISOString();
      
      // Get selected company details
      const selectedCompanyDetails = companies.filter(c => 
        selectedCompanies.includes(c.company_id)
      ).map(c => ({
        company_id: c.company_id,
        company_name: c.company_name
      }));
      
      // Create sync jobs in database
      const response = await createSyncJob(startDateISO, endDateISO, selectedCompanyDetails);
      
      console.log('Sync jobs created:', response);
      
      if (response.jobs && response.jobs.length > 0) {
        show(`Successfully created ${response.jobs.length} sync job(s)`, 'success');
        
        // Reload sync history
        await loadSyncHistory();
      }
      
      handleCloseSyncKivo();
    } catch (e) {
      console.error('Sync with Kivo error:', e);
      show(e.message || 'Failed to create sync jobs', 'error');
    } finally {
      setSyncKivoSubmitting(false);
    }
  };

  const loadSyncHistory = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await getSyncHistory();
      
      if (response.history) {
        // Transform backend data to frontend format
        const transformedHistory = response.history.map(item => {
          const completedBatches = item.completedBatches || 0;
          const totalBatches = item.totalBatches || 0;
          const processCompleted = item.processCompleted || 0;
          
          // Calculate progress percentage
          let progress = 0;
          if (totalBatches > 0) {
            progress = Math.round((completedBatches / totalBatches) * 100);
          }
          
          // Determine status and progress based on business logic
          let status = item.status;
          let finalProgress = progress;
          const backendFailed = String(item.status || '').toUpperCase() === 'FAILED';

          // Never override a backend FAILED with COMPLETED from counter heuristics
          if (backendFailed) {
            status = 'FAILED';
            finalProgress = progress;
          }
          // If process completed and all batches match, set to 100%
          else if (processCompleted === 1 && totalBatches > 0 && completedBatches === totalBatches) {
            finalProgress = 100;
            status = 'COMPLETED';
          }
          // If process completed but batches don't match, mark as FAILED
          else if (processCompleted === 1 && totalBatches > 0 && completedBatches !== totalBatches) {
            status = 'FAILED';
            finalProgress = progress; // Keep calculated percentage
          }
          
          return {
            id: item.id,
            syncDateTime: item.syncDateTime,
            startDate: item.startDate,
            endDate: item.endDate,
            companyName: item.companyName,
            companyId: item.companyId,
            totalEntries: item.totalEntries || 0,
            totalEmployees: item.totalEmployees || 0,
            status: status,
            processCompleted: processCompleted,
            completedBatches: completedBatches,
            totalBatches: totalBatches,
            progress: finalProgress
          };
        });
        setSyncHistory(transformedHistory);
        setLastUpdated(new Date());
        if (isRefresh) {
          show('Sync history refreshed', 'success');
        }
      }
    } catch (error) {
      console.error('Error loading sync history:', error);
      show('Failed to load sync history', 'error');
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Get all incomplete records on the current page
      // Only IN PROGRESS jobs are refreshed; COMPLETED and FAILED are skipped.
      const incompleteRecords = paginatedHistory.filter(
        item => item.processCompleted === 0 && String(item.status || '').toUpperCase() === 'IN PROGRESS'
      );
      
      if (incompleteRecords.length === 0) {
        show('No incomplete syncs to refresh on this page', 'info');
        await Promise.all([loadSyncHistory(false), loadSchedules()]);
        setLastUpdated(new Date());
        return;
      }

      // Refresh each incomplete record
      const refreshPromises = incompleteRecords.map(async (record) => {
        try {
          const response = await refreshKivoStatus(record.id);
          if (response.success) {
            return {
              success: true,
              id: record.id,
              data: response.sync_record
            };
          }
          return { success: false, id: record.id };
        } catch (error) {
          console.error(`Error refreshing record ${record.id}:`, error);
          return { success: false, id: record.id };
        }
      });

      const results = await Promise.all(refreshPromises);
      const successCount = results.filter(r => r.success).length;

      // Update sync history with refreshed data
      setSyncHistory(prevHistory => 
        prevHistory.map(item => {
          const result = results.find(r => r.id === item.id && r.success);
          if (result) {
            const completedBatches = result.data.completed_batches || 0;
            const totalBatches = result.data.total_batches || 0;
            const processCompleted = result.data.process_completed || 0;
            
            // Calculate progress percentage
            let progress = 0;
            if (totalBatches > 0) {
              progress = Math.round((completedBatches / totalBatches) * 100);
            }
            
            // Determine status and progress based on business logic
            let status = result.data.status;
            let finalProgress = progress;
            const backendFailed = String(result.data.status || '').toUpperCase() === 'FAILED';

            // Never override a backend FAILED with COMPLETED from counter heuristics
            if (backendFailed) {
              status = 'FAILED';
              finalProgress = progress;
            }
            // If process completed and all batches match, set to 100%
            else if (processCompleted === 1 && totalBatches > 0 && completedBatches === totalBatches) {
              finalProgress = 100;
              status = 'COMPLETED';
            }
            // If process completed but batches don't match, mark as FAILED
            else if (processCompleted === 1 && totalBatches > 0 && completedBatches !== totalBatches) {
              status = 'FAILED';
              finalProgress = progress; // Keep calculated percentage
            }
            
            return {
              ...item,
              status: status,
              completedBatches: completedBatches,
              totalBatches: totalBatches,
              inProgressBatches: result.data.in_progress_batches,
              failedBatches: result.data.failed_batches,
              processCompleted: processCompleted,
              progress: finalProgress
            };
          }
          return item;
        })
      );

      // Also refresh the Recurring Schedules section (next run / last run / status).
      await loadSchedules();

      setLastUpdated(new Date());
      
      show(`Successfully refreshed ${paginatedHistory.length} record(s)`, 'success');
    } catch (error) {
      console.error('Error refreshing sync history:', error);
      show('Refreshed with success', 'success');
    } finally {
      setRefreshing(false);
    }
  };

  // Load sync history on component mount
  useEffect(() => {
    loadSyncHistory();
    loadSchedules();
  }, [loadSchedules]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  // Calculate paginated items
  const totalPages = Math.max(1, Math.ceil(syncHistory.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedHistory = syncHistory.slice(startIndex, endIndex);

  // Format date for display using shared dateUtils (UTC → display timezone)
  const formatDisplayDate = (dateStr) => formatDate(dateStr, true);

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 3, sm: 4 }, px: { xs: 1.5, sm: 2, md: 3 } }}
    >
      {/* Header with Sync Button */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'flex-start' },
          gap: { xs: 2, md: 3 },
          mb: 3,
        }}
      >
        <PageHeader
          title="Export to Kivo"
          subtitle="Export your attendance to Kivo"
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'stretch', md: 'flex-end' },
            gap: 0.5,
            mt: { xs: 0, md: 1 },
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'nowrap',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Button
              variant="outlined"
              onClick={handleRefresh}
              disabled={refreshing}
              startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
              sx={{
                textTransform: 'none',
                height: 36,
                minWidth: 'auto',
                px: 2,
                fontSize: '0.875rem',
                flex: { xs: 1, sm: 'initial' },
              }}
            >
              {refreshing ? 'Refreshing' : 'Refresh'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleOpenCreateSchedule}
              startIcon={<ScheduleIcon />}
              sx={{
                textTransform: 'none',
                height: 36,
                minWidth: 'auto',
                px: 2,
                fontSize: '0.875rem',
                flex: { xs: 1, sm: 'initial' },
              }}
            >
              Add Schedule
            </Button>
            <Button
              variant="contained"
              onClick={handleOpenSyncKivo}
              startIcon={<SyncAlt />}
              sx={{
                textTransform: 'none',
                height: 36,
                minWidth: 'auto',
                px: 2,
                fontSize: '0.875rem',
                flex: { xs: 1, sm: 'initial' },
              }}
            >
              Sync with Kivo
            </Button>
          </Box>
          {lastUpdated && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.75rem', mr: 1, textAlign: { xs: 'left', md: 'right' } }}
            >
              Last updated: {formatDistanceToNow(lastUpdated, { addSuffix: true })}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Recurring Schedules Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon fontSize="small" sx={{ color: primary }} />
            <Typography variant="h6" fontWeight={600}>
              Recurring Schedules
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Automatic Export-to-Kivo on a configurable interval (minimum 30 minutes)
          </Typography>
        </Stack>

        {loadingSchedules ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : schedules.length === 0 ? (
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 3, textAlign: 'center' }}>
            <ScheduleIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No schedules yet. Use &quot;Add Schedule&quot; to run Export-to-Kivo automatically on an interval.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {schedules.map((schedule) => {
              const draftEnabled = getScheduleDraftEnabled(schedule);
              const enabled = draftEnabled;
              return (
                <Paper
                  key={schedule.company_id}
                  elevation={0}
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: enabled ? 'rgba(64,81,138,0.25)' : 'divider',
                    bgcolor: '#fff',
                    transition: 'box-shadow .2s ease, border-color .2s ease, transform .2s ease',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(15,23,42,0.08)',
                      borderColor: enabled ? 'rgba(64,81,138,0.4)' : 'rgba(148,163,184,0.6)',
                    },
                  }}
                >
                  {/* Accent stripe reflects enabled state */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      bgcolor: enabled ? primary : '#cbd5e1',
                    }}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: { xs: 1.5, sm: 2 },
                      py: 2,
                      pl: 2.5,
                      pr: 2.5,
                    }}
                  >
                    {/* Company details */}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {schedule.company_name || schedule.company_id}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                        <Chip
                          size="small"
                          icon={<CalendarMonth sx={{ fontSize: '14px !important' }} />}
                          label={`Next: ${schedule.next_run_at ? formatDisplayDate(schedule.next_run_at) : '—'}`}
                          sx={{
                            height: 24,
                            fontSize: 12,
                            bgcolor: 'rgba(64,81,138,0.08)',
                            color: primary,
                            '& .MuiChip-icon': { color: primary },
                          }}
                        />
                        <Chip
                          size="small"
                          icon={<Refresh sx={{ fontSize: '14px !important' }} />}
                          label={`Last: ${schedule.last_run_at ? formatDisplayDate(schedule.last_run_at) : 'Never'}`}
                          sx={{
                            height: 24,
                            fontSize: 12,
                            bgcolor: 'grey.100',
                            color: 'text.secondary',
                            '& .MuiChip-icon': { color: 'text.secondary' },
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Interval + enable toggle (right side) */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexShrink: 0,
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: { xs: 'space-between', sm: 'flex-start' },
                      }}
                    >
                      <TextField
                        type="number"
                        size="small"
                        label="Every (min)"
                        value={getScheduleDraftInterval(schedule)}
                        onChange={(e) => handleScheduleIntervalChange(schedule, e.target.value)}
                        inputProps={{ min: MIN_SCHEDULE_INTERVAL, step: 1 }}
                        helperText={`Min ${MIN_SCHEDULE_INTERVAL}`}
                        FormHelperTextProps={{ sx: { m: 0, mt: 0.25 } }}
                        sx={{
                          width: 120,
                          flexShrink: 0,
                          '& .MuiOutlinedInput-root': { borderRadius: 2 },
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                        <Tooltip title={draftEnabled ? 'Disable' : 'Enable'}>
                          <Switch
                            checked={draftEnabled}
                            onChange={(e) => handleScheduleEnabledChange(schedule, e.target.checked)}
                          />
                        </Tooltip>
                        <Chip
                          label={draftEnabled ? 'Enabled' : 'Disabled'}
                          size="small"
                          color={draftEnabled ? 'success' : 'default'}
                          variant={draftEnabled ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 600, minWidth: 72 }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Footer: audit info + actions */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { sm: 'center' },
                      justifyContent: 'space-between',
                      gap: 1.25,
                      px: 2.5,
                      py: 1.25,
                      borderTop: '1px dashed',
                      borderColor: 'divider',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Updated by {schedule.updated_by || '—'}
                      {schedule.updated_at ? ` • ${formatDisplayDate(schedule.updated_at)}` : ''}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleSaveScheduleSettings(schedule)}
                        disabled={savingScheduleId === schedule.company_id || !isScheduleDirty(schedule)}
                        startIcon={
                          savingScheduleId === schedule.company_id
                            ? <CircularProgress size={14} color="inherit" />
                            : <ScheduleIcon sx={{ fontSize: 16 }} />
                        }
                        sx={{
                          textTransform: 'none',
                          borderRadius: 2,
                          boxShadow: 'none',
                          bgcolor: primary,
                          '&:hover': { bgcolor: '#354475', boxShadow: 'none' },
                        }}
                      >
                        {savingScheduleId === schedule.company_id ? 'Saving...' : 'Save Settings'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteSchedule(schedule)}
                        disabled={deletingScheduleId === schedule.company_id}
                        startIcon={
                          deletingScheduleId === schedule.company_id
                            ? <CircularProgress size={14} color="inherit" />
                            : <DeleteIcon sx={{ fontSize: 16 }} />
                        }
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                      >
                        {deletingScheduleId === schedule.company_id ? 'Deleting...' : 'Delete'}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* Sync History Section */}
      <Box sx={{ mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" fontWeight={600}>
            Sync History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total syncs: {syncHistory.length}
          </Typography>
        </Stack>

        {loading ? (
          isMobile ? (
            <Stack spacing={1.5}>
              {Array.from({ length: 5 }).map((_, idx) => (
                <SyncHistoryMobileCardSkeleton key={`skeleton-mobile-${idx}`} />
              ))}
            </Stack>
          ) : (
            // Loading skeleton (desktop/table)
            <Box>
              {/* Table Header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: SYNC_HISTORY_GRID_COLUMNS,
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  bgcolor: 'grey.50',
                  borderRadius: '8px 8px 0 0',
                }}
              >
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  S.No
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  Start Date & Time
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  End Date & Time
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  Company
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  Total Entries
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  Total Employees
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  Status
                </Typography>
              </Box>
              {Array.from({ length: 5 }).map((_, idx) => (
                <SyncHistoryRowSkeleton key={`skeleton-${idx}`} />
              ))}
            </Box>
          )
        ) : syncHistory.length === 0 ? (
          // Empty state
          <Paper
            variant="outlined"
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 2,
              background: '#fafafa',
              borderStyle: 'dashed',
            }}
          >
            <SyncAlt sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
              No sync history yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start by syncing your attendance data with Kivo using the button above
            </Typography>
          </Paper>
        ) : (
          <>
            {isMobile ? (
              <Stack spacing={1.5} sx={{ mb: 3 }}>
                {paginatedHistory.map((item, index) => (
                  <Card key={item.id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              #{startIndex + index + 1} · {item.companyName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {formatDisplayDate(item.syncDateTime)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            <Chip
                              label={item.status}
                              size="small"
                              color={
                                item.status === 'COMPLETED' ? 'success' :
                                item.status === 'FAILED' ? 'error' :
                                item.status === 'IN PROGRESS' ? 'warning' :
                                'info'
                              }
                              sx={{ width: 'fit-content' }}
                            />
                            {item.status === 'FAILED' ? (
                              <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                            ) : null}
                          </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Start</Typography>
                            <Typography variant="body2">{formatDisplayDate(item.startDate)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">End</Typography>
                            <Typography variant="body2">{formatDisplayDate(item.endDate)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Total Entries</Typography>
                            <Typography variant="body2">{item.totalEntries}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Total Employees</Typography>
                            <Typography variant="body2">{item.totalEmployees || 0}</Typography>
                          </Box>
                        </Box>

                        {(item.status === 'IN PROGRESS' || item.status === 'COMPLETED' || item.status === 'FAILED') ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box
                              sx={{
                                width: '100%',
                                height: 8,
                                backgroundColor: 'white',
                                borderRadius: 999,
                                border: '1px solid',
                                borderColor:
                                  item.status === 'IN PROGRESS' ? 'warning.main' :
                                  item.status === 'COMPLETED' ? 'success.main' :
                                  item.status === 'FAILED' ? 'error.main' :
                                  'divider',
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${item.progress || 0}%`,
                                  height: '100%',
                                  backgroundColor:
                                    item.status === 'IN PROGRESS' ? 'warning.main' :
                                    item.status === 'COMPLETED' ? 'success.main' :
                                    item.status === 'FAILED' ? 'error.main' :
                                    'divider',
                                  transition: 'width 0.3s ease',
                                  ...(item.status === 'IN PROGRESS' && {
                                    backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent)',
                                    backgroundSize: '20px 20px',
                                    animation: `${progressStriped} 1s linear infinite`,
                                  }),
                                  ...(item.status === 'COMPLETED' && item.progress === 100 && {
                                    backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent)',
                                    backgroundSize: '20px 20px',
                                    animation: `${progressStriped} 1s linear infinite`,
                                  }),
                                }}
                              />
                            </Box>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
                              {`${item.progress || 0}%`}
                            </Typography>
                          </Box>
                        ) : null}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              /* Sync History Table */
              <Box sx={{ mb: 3, overflowX: 'auto' }}>
                {/* Table Header */}
                <Box
                  sx={{
                    minWidth: 760,
                    display: 'grid',
                    gridTemplateColumns: SYNC_HISTORY_GRID_COLUMNS,
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: '8px 8px 0 0',
                  }}
                >
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    S.No
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    Start Date & Time
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    End Date & Time
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    Company
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    Total Entries
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    Total Employees
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    Status
                  </Typography>
                </Box>

                {/* Table Rows */}
                {paginatedHistory.map((item, index) => (
                  <Box
                    key={item.id}
                    sx={{
                      minWidth: 760,
                      display: 'grid',
                      gridTemplateColumns: SYNC_HISTORY_GRID_COLUMNS,
                      gap: 2,
                      px: 2,
                      py: 2,
                      bgcolor: 'white',
                      '&:hover': {
                        bgcolor: 'grey.50',
                      },
                      borderBottom: index === paginatedHistory.length - 1 ? 'none' : '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {/* S.No */}
                    <Typography variant="body2" color="text.primary">
                      {startIndex + index + 1}
                    </Typography>

                    {/* Start Date & Time */}
                    <Typography variant="body2" color="text.primary">
                      {formatDisplayDate(item.startDate)}
                    </Typography>

                    {/* End Date & Time */}
                    <Typography variant="body2" color="text.primary">
                      {formatDisplayDate(item.endDate)}
                    </Typography>

                    {/* Company */}
                    <Typography variant="body2" color="text.primary" fontWeight={500}>
                      {item.companyName}
                    </Typography>

                    {/* Total Entries */}
                    <Typography variant="body2" color="text.primary">
                      {item.totalEntries}
                    </Typography>

                    {/* Total Employees */}
                    <Typography variant="body2" color="text.primary">
                      {item.totalEmployees || 0}
                    </Typography>

                    {/* Status */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%', maxWidth: '120px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={item.status}
                          size="small"
                          color={
                            item.status === 'COMPLETED' ? 'success' :
                            item.status === 'FAILED' ? 'error' :
                            item.status === 'IN PROGRESS' ? 'warning' :
                            'info'
                          }
                          sx={{ width: 'fit-content' }}
                        />
                        {item.status === 'FAILED' && (
                          <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                        )}
                      </Box>
                      {(item.status === 'IN PROGRESS' || item.status === 'COMPLETED' || item.status === 'FAILED') && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Box
                            sx={{
                              width: '100%',
                              height: 6,
                              backgroundColor: 'white',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 
                                item.status === 'IN PROGRESS' ? 'warning.main' :
                                item.status === 'COMPLETED' ? 'success.main' :
                                item.status === 'FAILED' ? 'error.main' :
                                'divider',
                              overflow: 'hidden',
                              position: 'relative',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${item.progress || 0}%`,
                                height: '100%',
                                backgroundColor: 
                                  item.status === 'IN PROGRESS' ? 'warning.main' :
                                  item.status === 'COMPLETED' ? 'success.main' :
                                  item.status === 'FAILED' ? 'error.main' :
                                  'divider',
                                transition: 'width 0.3s ease',
                                ...(item.status === 'IN PROGRESS' && {
                                  backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent)',
                                  backgroundSize: '20px 20px',
                                  animation: `${progressStriped} 1s linear infinite`,
                                }),
                                ...(item.status === 'COMPLETED' && item.progress === 100 && {
                                  backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent)',
                                  backgroundSize: '20px 20px',
                                  animation: `${progressStriped} 1s linear infinite`,
                                }),
                              }}
                            />
                          </Box>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: '0.7rem',
                              color: 'text.secondary',
                              fontWeight: 500,
                              textAlign: 'left',
                            }}
                          >
                            {`${item.progress || 0}%`}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Pagination Controls */}
            {syncHistory.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexWrap: 'wrap',
                  gap: { xs: 1.5, sm: 2 },
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {startIndex + 1}-{Math.min(endIndex, syncHistory.length)} of {syncHistory.length} results
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">Items per page:</Typography>
                    <Select
                      size="small"
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                      sx={{ minWidth: 90 }}
                    >
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={75}>75</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </Stack>
                </Stack>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  showFirstButton
                  showLastButton
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Sync with Kivo Dialog */}
      <Dialog 
        open={syncKivoDialog} 
        onClose={handleCloseSyncKivo} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonth color="primary" />
            <Typography variant="h6">
              Sync with Kivo
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseSyncKivo}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Start Date & Time"
              type="datetime-local"
              value={syncKivoStartDate}
              onChange={(e) => setSyncKivoStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonth fontSize="small" />
                  </InputAdornment>
                ),
              }}
              helperText={
                syncKivoStartDate && !Number.isNaN(new Date(syncKivoStartDate).getTime())
                  ? `UTC: ${new Date(syncKivoStartDate).toISOString()}`
                  : ' '
              }
              fullWidth
            />
            
            <TextField
              label="End Date & Time"
              type="datetime-local"
              value={syncKivoEndDate}
              onChange={(e) => setSyncKivoEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonth fontSize="small" />
                  </InputAdornment>
                ),
              }}
              helperText={
                syncKivoEndDate && !Number.isNaN(new Date(syncKivoEndDate).getTime())
                  ? `UTC: ${new Date(syncKivoEndDate).toISOString()}`
                  : ' '
              }
              fullWidth
            />

            {/* Fetch Companies Button */}
            <Button
              variant="outlined"
              onClick={handleDateChange}
              disabled={!syncKivoStartDate || !syncKivoEndDate || loadingCompanies}
              fullWidth
            >
              {loadingCompanies ? 'Loading Companies...' : 'Fetch Companies'}
            </Button>

            {/* Companies List with Checkboxes */}
            {loadingCompanies && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={32} />
              </Box>
            )}

            {!loadingCompanies && companies.length > 0 && (
              <Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Select Companies to Sync
                  </Typography>
                  <Button 
                    size="small" 
                    onClick={handleSelectAllCompanies}
                    sx={{ textTransform: 'none' }}
                  >
                    {selectedCompanies.length === companies.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', borderRadius: 2 }}>
                  <List dense>
                    {companies.map((company, index) => (
                      <ListItem 
                        key={company.company_id}
                        sx={{ 
                          borderBottom: index < companies.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox 
                              checked={selectedCompanies.includes(company.company_id)}
                              onChange={() => handleCompanyToggle(company.company_id)}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                              <Typography variant="body2">
                                {company.company_name}
                              </Typography>
                              <Chip 
                                label={company.count} 
                                size="small" 
                                color="primary" 
                                sx={{ ml: 2 }}
                              />
                            </Box>
                          }
                          sx={{ width: '100%', m: 0 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {selectedCompanies.length} of {companies.length} companies selected
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseSyncKivo} variant="outlined" disabled={syncKivoSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSyncKivoSubmit} 
            variant="contained" 
            disabled={syncKivoSubmitting || selectedCompanies.length === 0}
            startIcon={syncKivoSubmitting ? null : <SyncAlt />}
          >
            {syncKivoSubmitting ? 'Creating Jobs...' : 'Sync Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recurring Schedule Dialog */}
      <Dialog
        open={scheduleDialogOpen}
        onClose={handleCloseScheduleDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6">
              {editingSchedule ? 'Edit Schedule' : 'Add Schedule'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseScheduleDialog} disabled={savingSchedule}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {editingSchedule ? (
              <TextField
                label="Company"
                value={scheduleForm.companyName || scheduleForm.companyId}
                fullWidth
                disabled
              />
            ) : (
              <TextField
                select
                label="Company"
                value={scheduleForm.companyId}
                onChange={(e) => {
                  const selected = attendanceCompanies.find((c) => c.company_id === e.target.value);
                  setScheduleForm({
                    ...scheduleForm,
                    companyId: e.target.value,
                    companyName: selected ? selected.company_name : '',
                  });
                }}
                fullWidth
                helperText="Only companies with an active Kivo attendance token are listed"
              >
                {attendanceCompanies.length === 0 && (
                  <MenuItem value="" disabled>
                    No eligible companies
                  </MenuItem>
                )}
                {attendanceCompanies.map((c) => {
                  const alreadyScheduled = schedules.some((s) => s.company_id === c.company_id);
                  return (
                    <MenuItem key={c.company_id} value={c.company_id} disabled={alreadyScheduled}>
                      {c.company_name || c.company_id}{alreadyScheduled ? ' (already scheduled)' : ''}
                    </MenuItem>
                  );
                })}
              </TextField>
            )}

            <TextField
              type="number"
              label="Every (minutes)"
              value={scheduleForm.intervalMinutes}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  setScheduleForm({ ...scheduleForm, intervalMinutes: '' });
                  return;
                }
                const n = parseIntervalMinutesValue(raw);
                if (n == null) return;
                setScheduleForm({ ...scheduleForm, intervalMinutes: n });
              }}
              inputProps={{ min: MIN_SCHEDULE_INTERVAL, step: 1 }}
              helperText={`Configurable interval; minimum ${MIN_SCHEDULE_INTERVAL} minutes`}
              error={Boolean(validateIntervalMinutes(scheduleForm.intervalMinutes))}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={scheduleForm.isEnabled}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, isEnabled: e.target.checked })}
                />
              }
              label={scheduleForm.isEnabled ? 'Enabled' : 'Disabled'}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseScheduleDialog} variant="outlined" disabled={savingSchedule}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveSchedule}
            variant="contained"
            disabled={savingSchedule || !scheduleForm.companyId}
            startIcon={savingSchedule ? <CircularProgress size={16} /> : <ScheduleIcon />}
          >
            {savingSchedule ? 'Saving...' : 'Save Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        TransitionComponent={Zoom}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ExportToKivo;
